/**
 * --------------------------------------------------------------------------
 * CoreUI PRO data-grid ui/body.ts
 * License (https://coreui.io/pro/license/)
 * --------------------------------------------------------------------------
 */

import type { GridVirtualizerInstance } from '../engine/virtualizer'
import type { DataGridColumn, DataGridLabels, DataGridRow } from '../types'
import { applyPinnedCell, type GridPinning } from './pinning'

const CLASS_NAME_EMPTY = 'data-grid-empty'
const FOCUSABLE_SELECTOR = 'a[href], button, input, select, textarea, [tabindex]'
const CLASS_NAME_ROW_ODD = 'data-grid-row-odd'
const CLASS_NAME_ROW_SELECTED = 'table-active'
const CLASS_NAME_CELL_SELECTED = 'data-grid-cell-selected'
const CLASS_NAME_HANDLE_CELL = 'data-grid-row-handle-cell'
const EDGE_CLASS_NAMES = ['top', 'right', 'bottom', 'left'] as const
const CLASS_NAME_SELECT_CELL = 'data-grid-select-cell'
const CLASS_NAME_SPACER = 'data-grid-spacer'

export interface GridBodyContext {
  // Hands every rendered row to the virtualizer for measurement instead of
  // assuming a fixed rowHeight.
  autoRowHeight: boolean
  // With cellNavigation on, rows/cells carry grid roles, every cell (and the
  // interactive content inside it) drops out of the tab order, and cells are
  // addressable by data-coreui-column - the roving tabindex pass in the grid
  // root owns which single cell is tabbable.
  cellNavigation: boolean
  headerRowCount: number
  labels: DataGridLabels
  getDataVersion: () => number
  // Inline editing: the cell being edited (keyed by rowId + columnId, NOT
  // index - recycling changes indexes) renders the editor element instead of
  // its content until commit/cancel. Null when editing is off or idle.
  getEditingCell: () => { rowId: string; columnId: string } | null
  getEditorElement: () => HTMLElement | null
  getPinning: () => GridPinning | null
  // Absolute index of the page's first row (pagination mode), else 0 - keeps
  // aria-rowindex absolute like aria-rowcount.
  getRowIndexOffset: () => number
  getRows: () => DataGridRow[]
  // Visible leaf columns in table order (pinning + columnOrder + visibility).
  getVisibleColumns: () => DataGridColumn[]
  getVirtualizer: () => GridVirtualizerInstance | null
  renderEmpty: () => Node | string
  // Guards every string injected via innerHTML (render results, empty state).
  sanitizeContent: (html: string) => string
  cellSelection: boolean
  rowOrder: boolean
  rowHandleIcon: string
  selection: boolean
}

export interface GridBody {
  element: HTMLTableSectionElement
  update: () => void
}

const setCellContent = (
  cellElement: HTMLTableCellElement,
  content: Node | string,
  sanitizeContent: (html: string) => string
): void => {
  if (typeof content === 'string') {
    cellElement.innerHTML = sanitizeContent(content)
    return
  }

  // Re-inserting the same node detaches it (losing focus/selection in a custom
  // empty slot); skip when it is already the sole child.
  if (cellElement.childNodes.length === 1 && cellElement.firstChild === content) {
    return
  }

  cellElement.replaceChildren(content)
}

export const createBody = (context: GridBodyContext): GridBody => {
  const element = document.createElement('tbody')

  // Snapshot of the current layout, refreshed once per update() — cells are
  // rebuilt (not recycled) when the visible column set or order changes.
  let currentColumns: DataGridColumn[] = context.getVisibleColumns()
  let layoutSignature = currentColumns.map(column => column.key).join('|')
  // The handle column and the select column both sit before the data columns;
  // every cell index in this module is offset by however many are on.
  const leadingCount = (context.rowOrder ? 1 : 0) + (context.selection ? 1 : 0)
  let cellCount = currentColumns.length + leadingCount

  const buildRowCells = (rowElement: HTMLTableRowElement): void => {
    rowElement.replaceChildren()

    if (context.cellNavigation) {
      rowElement.setAttribute('role', 'row')
    }

    const offset = leadingCount

    if (context.rowOrder) {
      const cellElement = document.createElement('td')
      cellElement.classList.add(CLASS_NAME_HANDLE_CELL)
      const handle = document.createElement('span')
      handle.classList.add('data-grid-row-handle')
      handle.dataset.coreuiRowHandle = ''
      handle.setAttribute('aria-hidden', 'true')
      handle.innerHTML = context.rowHandleIcon
      cellElement.append(handle)
      if (context.cellNavigation) {
        cellElement.setAttribute('role', 'gridcell')
        cellElement.setAttribute('aria-colindex', '1')
      }

      rowElement.append(cellElement)
    }

    if (context.selection) {
      const cellElement = document.createElement('td')
      cellElement.classList.add(CLASS_NAME_SELECT_CELL)
      const input = document.createElement('input')
      input.type = 'checkbox'
      input.classList.add('form-check-input')
      input.setAttribute('aria-label', context.labels.selectRow)
      input.dataset.coreuiRowSelect = ''
      if (context.cellNavigation) {
        cellElement.setAttribute('role', 'gridcell')
        cellElement.setAttribute('aria-colindex', String(context.rowOrder ? 2 : 1))
        input.tabIndex = -1
      }

      cellElement.append(input)
      rowElement.append(cellElement)
    }

    for (const [index, column] of currentColumns.entries()) {
      const cellElement = document.createElement('td')
      if (context.cellNavigation) {
        cellElement.setAttribute('role', 'gridcell')
        cellElement.setAttribute('aria-colindex', String(offset + index + 1))
        cellElement.dataset.coreuiColumn = column.key
        cellElement.tabIndex = -1
      }

      rowElement.append(cellElement)
    }

    rowElement.dataset.coreuiLayout = layoutSignature
  }

  const createRowElement = (): HTMLTableRowElement => {
    const rowElement = document.createElement('tr')
    buildRowCells(rowElement)
    return rowElement
  }

  const updateRowCells = (rowElement: HTMLTableRowElement, row: DataGridRow, index: number): void => {
    const offset = leadingCount
    const editingCell = context.getEditingCell()

    for (const [cellIndex, column] of currentColumns.entries()) {
      const cellElement = rowElement.children[cellIndex + offset] as HTMLTableCellElement

      if (editingCell && editingCell.rowId === row.id && editingCell.columnId === column.key) {
        const editorElement = context.getEditorElement()
        if (editorElement) {
          cellElement.replaceChildren(editorElement)
          continue
        }
      }

      if (column.render) {
        setCellContent(cellElement, column.render(row.original, index), context.sanitizeContent)
        if (context.cellNavigation) {
          for (const focusable of cellElement.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)) {
            focusable.tabIndex = -1
          }
        }

        continue
      }

      const value = row.getValue(column.key)
      cellElement.textContent = column.formatter ?
        column.formatter(value, row.original) :
        String(value ?? '')
    }
  }

  const updateRowElement = (rowElement: HTMLTableRowElement, row: DataGridRow, index: number): void => {
    if (rowElement.dataset.coreuiLayout !== layoutSignature) {
      buildRowCells(rowElement)
      delete rowElement.dataset.coreuiRow
    }

    if (context.selection) {
      const selectCell = rowElement.children[leadingCount - 1] as HTMLTableCellElement
      const input = selectCell.firstElementChild as HTMLInputElement
      const selected = row.getIsSelected()
      input.checked = selected
      rowElement.classList.toggle(CLASS_NAME_ROW_SELECTED, selected)
      rowElement.setAttribute('aria-selected', String(selected))
    }

    // Cell content is immutable for a given (data version, row id, index)
    // triple — data replacements (dataProvider loads, update()) bump the
    // version — so recycled elements skip the cell pass. This keeps custom
    // render callbacks off the scroll hot path, where all three are stable.
    const rowKey = `${context.getDataVersion()}:${row.id}`
    if (rowElement.dataset.coreuiRow !== rowKey || rowElement.dataset.coreuiIndex !== String(index)) {
      updateRowCells(rowElement, row, index)
      rowElement.dataset.coreuiRow = rowKey
    }

    // Selection is repainted on every pass, unlike cell content: it changes
    // without the row's data version or id changing.
    if (context.cellSelection) {
      const cells = new Map(row.getAllCells().map(cell => [cell.column.id, cell]))
      for (const [cellIndex, column] of currentColumns.entries()) {
        const cellElement = rowElement.children[cellIndex + leadingCount] as HTMLTableCellElement
        const cell = cells.get(column.key)
        const selected = cell?.getIsSelected() ?? false
        cellElement.classList.toggle(CLASS_NAME_CELL_SELECTED, selected)
        const edges = selected && cell ? cell.getSelectionEdges() : null
        for (const edge of EDGE_CLASS_NAMES) {
          cellElement.classList.toggle(`${CLASS_NAME_CELL_SELECTED}-${edge}`, Boolean(edges?.[edge]))
        }
      }
    }

    rowElement.dataset.coreuiIndex = String(index)
    rowElement.setAttribute('aria-rowindex', String(context.headerRowCount + context.getRowIndexOffset() + index + 1))
    rowElement.classList.toggle(CLASS_NAME_ROW_ODD, index % 2 === 1)

    if (currentPinning) {
      const pinning = currentPinning
      const offset = leadingCount
      // Leading cells share the select column's pin — they travel together at
      // the start edge.
      for (let cellIndex = 0; cellIndex < leadingCount; cellIndex++) {
        applyPinnedCell(rowElement.children[cellIndex] as HTMLTableCellElement, pinning.select)
      }

      for (const [cellIndex, column] of currentColumns.entries()) {
        applyPinnedCell(rowElement.children[cellIndex + offset] as HTMLTableCellElement, pinning.columns[column.key] ?? null)
      }
    }
  }

  const createSpacer = (): HTMLTableRowElement => {
    const rowElement = document.createElement('tr')
    rowElement.classList.add(CLASS_NAME_SPACER)
    rowElement.setAttribute('aria-hidden', 'true')

    const cellElement = document.createElement('td')
    cellElement.colSpan = Math.max(cellCount, 1)
    rowElement.append(cellElement)

    return rowElement
  }

  const setSpacerHeight = (spacerElement: HTMLTableRowElement, height: number): void => {
    spacerElement.style.height = `${height}px`;
    (spacerElement.firstElementChild as HTMLTableCellElement).style.height = `${height}px`
    spacerElement.style.display = height > 0 ? '' : 'none'
  }

  const topSpacer = createSpacer()
  const bottomSpacer = createSpacer()
  let spacersAttached = false
  let emptyElement: HTMLTableRowElement | null = null
  let rowElements: HTMLTableRowElement[] = []
  let currentPinning: GridPinning | null = null
  const rowPool: HTMLTableRowElement[] = []

  const renderEmpty = () => {
    spacersAttached = false
    rowPool.push(...rowElements)
    rowElements = []

    if (!emptyElement) {
      emptyElement = document.createElement('tr')
      emptyElement.classList.add(CLASS_NAME_EMPTY)
      emptyElement.append(document.createElement('td'))
    }

    const cellElement = emptyElement.firstElementChild as HTMLTableCellElement
    cellElement.colSpan = Math.max(cellCount, 1)
    setCellContent(cellElement, context.renderEmpty(), context.sanitizeContent)

    element.replaceChildren(emptyElement)
  }

  const renderAll = (rows: DataGridRow[]) => {
    if (emptyElement) {
      emptyElement.remove()
    }

    while (rowElements.length > rows.length) {
      const rowElement = rowElements.pop()!
      rowElement.remove()
      rowPool.push(rowElement)
    }

    while (rowElements.length < rows.length) {
      const rowElement = rowPool.pop() ?? createRowElement()
      element.append(rowElement)
      rowElements.push(rowElement)
    }

    for (const [index, row] of rows.entries()) {
      updateRowElement(rowElements[index], row, index)
    }
  }

  const renderWindow = (rows: DataGridRow[], virtualizer: GridVirtualizerInstance) => {
    if (!spacersAttached) {
      element.replaceChildren(topSpacer, bottomSpacer)
      spacersAttached = true
      rowElements = []
    }

    const virtualItems = virtualizer.getVirtualItems()
    const totalSize = virtualizer.getTotalSize()
    const paddingTop = virtualItems.length > 0 ? virtualItems[0].start : 0
    const paddingBottom = virtualItems.length > 0 ?
      totalSize - virtualItems[virtualItems.length - 1].end :
      totalSize

    setSpacerHeight(topSpacer, paddingTop)
    setSpacerHeight(bottomSpacer, paddingBottom)

    while (rowElements.length > virtualItems.length) {
      const rowElement = rowElements.pop()!
      rowElement.remove()
      rowPool.push(rowElement)
    }

    while (rowElements.length < virtualItems.length) {
      const rowElement = rowPool.pop() ?? createRowElement()
      bottomSpacer.before(rowElement)
      rowElements.push(rowElement)
    }

    for (const [index, virtualItem] of virtualItems.entries()) {
      const rowElement = rowElements[index]
      updateRowElement(rowElement, rows[virtualItem.index], virtualItem.index)

      if (context.autoRowHeight) {
        // The virtualizer resolves a measurement to its row through this
        // attribute, so it has to be current BEFORE the element is handed
        // over - recycled elements arrive carrying the previous index.
        rowElement.dataset.index = String(virtualItem.index)
        virtualizer.measureElement(rowElement)
      }
    }
  }

  let editingSignature: string | null = null

  return {
    element,
    update() {
      const rows = context.getRows()
      currentPinning = context.getPinning()

      // The immutable-cell contract skips the cell pass on recycled rows, so
      // an editing transition (start, commit, cancel, move) must force the
      // rendered rows to rebuild their cells - once, not per frame.
      const editingCell = context.getEditingCell()
      const nextEditingSignature = editingCell ? `${editingCell.rowId}:${editingCell.columnId}` : null
      if (nextEditingSignature !== editingSignature) {
        editingSignature = nextEditingSignature
        for (const rowElement of rowElements) {
          delete rowElement.dataset.coreuiRow
        }
      }

      const columns = context.getVisibleColumns()
      const signature = columns.map(column => column.key).join('|')
      if (signature !== layoutSignature) {
        currentColumns = columns
        layoutSignature = signature
        cellCount = currentColumns.length + leadingCount;
        (topSpacer.firstElementChild as HTMLTableCellElement).colSpan = Math.max(cellCount, 1);
        (bottomSpacer.firstElementChild as HTMLTableCellElement).colSpan = Math.max(cellCount, 1)
      }

      if (rows.length === 0) {
        renderEmpty()
        return
      }

      const virtualizer = context.getVirtualizer()

      if (virtualizer) {
        renderWindow(rows, virtualizer)
        return
      }

      renderAll(rows)
    }
  }
}
